import Vue from 'vue'
import jwtDecode from 'jwt-decode'

export interface User {
  id: number
  email: string
  designer_id: number
}

interface DecodedToken {
  user: {
    id: number
    email: string
    designer_id: number
  },
  iat: number
  exp: number
}

class ParseTokenError extends Error {
  status: number = 500
}

const TOKEN_KEY = 'token'

const UserBus = class extends Vue {
  public _user: User | null = null
  public token: string | null = null
  private decodedToken: DecodedToken | null = null

  get user () {
    return this._user
  }
  set user (user: User | null) {
    this._user = user
    this.$emit('user-update', user)
  }

  /**
   * custom function used to bootstrap UserBus (not the same as Vue's mounted function)
   * this is custom to allow the app to control when the UserBus gets mounted (in the main.ts)
   */
  mount () {
    try {
      const token = localStorage.getItem(TOKEN_KEY)
      if (token) {
        this.storeToken(token)
      }
    } catch (err) {
      console.error('Error parsing token in UserBus mount', err)
    }
  }

  /**
   * store token and user data
   * @param token encoded token
   */
  storeToken (token: string) {
    try {
      const decoded: DecodedToken = jwtDecode(token)

      this.decodedToken = decoded
      this.user = {
        id: decoded.user.id,
        email: decoded.user.email,
        designer_id: decoded.user.designer_id
      }

      this.token = token
      localStorage.setItem(TOKEN_KEY, token)
    } catch (err) {
      console.error('parsing token error. token may be corrupt. Please try re-logging. If this problems persist please contact For the Aisle support support@fortheaisle.com', err)
      throw new ParseTokenError()
    }
  }

  /**
   * remove token
   */
  removeToken () {
    this.token = null
    this.user = null
    localStorage.removeItem(TOKEN_KEY)
  }

  /**
   * determine if user is the specified designer by designer id
   * @param id designer id
   */
  userIsDesigner (id: number): boolean {
    return this.user && this.user.designer_id
      ? this.user.designer_id === id
      : false
  }

  /**
   * determine if token has expired
   */
  tokenHasExpired (): boolean {
    if (this.decodedToken) {
      const expire = this.decodedToken.exp
      const now = (new Date().getTime() / 1000)

      if (now > expire) {
        this.removeToken()
        return true
      } else {
        return false
      }
    } else {
      this.removeToken()
      return true
    }
  }
}

export default new UserBus()
